/** Load state of a zone-scoped tab: what the empty/all-clear copy is allowed to say. */
export type ZoneLoadState = "checking" | "loaded" | "stalled";

/** Progress of the work behind `checking`: history windows scanned, or seed attempts made. */
export interface ZoneLoadProgress { kind: "windows" | "attempts"; done: number; total: number }

/** Reactive load state for a zone-scoped tab. Starts `checking`; flips to `loaded` ONLY on a
 *  real signal (markLoaded: read-model seed success, the relay's caughtUp, a completed backfill).
 *  `stalled` ("can't reach the network yet, still trying") comes from `timeoutMs` of SILENCE —
 *  every progress() call re-arms the timer, so a slow but moving walk never stalls — or from an
 *  explicit markStalled (seed retries exhausted). A timer never produces `loaded`. From
 *  `stalled`, retry() starts a fresh `checking` cycle (the caller re-runs the work); it is a
 *  no-op in any other state so repeated taps cannot flood the relay. */
export class ZoneLoadGate {
  #state: ZoneLoadState = "checking";
  #progress: ZoneLoadProgress | undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;
  readonly #timeoutMs: number;
  readonly #onChange: () => void;
  constructor(timeoutMs: number, onChange: () => void) {
    this.#timeoutMs = timeoutMs;
    this.#onChange = onChange;
    this.#arm();
  }
  get state(): ZoneLoadState { return this.#state; }
  get loaded(): boolean { return this.#state === "loaded"; }
  /** Last reported progress of the current checking cycle (undefined once loaded / fresh). */
  get progress_(): ZoneLoadProgress | undefined { return this.#progress; }
  markLoaded(): void {
    if (this.#state === "loaded") return;
    this.#state = "loaded";
    this.#progress = undefined;
    this.#clearTimer();
    this.#onChange();
  }
  markStalled(): void {
    if (this.#state !== "checking") return; // loaded wins; stalled is idempotent
    this.#state = "stalled";
    this.#clearTimer();
    this.#onChange();
  }
  /** Work is moving: record it and re-arm the silence timer. Ignored once loaded. */
  progress(kind: ZoneLoadProgress["kind"], done: number, total: number): void {
    if (this.#state === "loaded") return;
    this.#progress = { kind, done, total };
    if (this.#state === "checking") this.#arm();
    this.#onChange();
  }
  /** Manual retry from `stalled`: back to a fresh `checking` cycle. */
  retry(): void {
    if (this.#state !== "stalled") return;
    this.#state = "checking";
    this.#progress = undefined;
    this.#arm();
    this.#onChange();
  }
  dispose(): void { this.#clearTimer(); }
  #arm(): void {
    this.#clearTimer();
    this.#timer = setTimeout(() => this.markStalled(), this.#timeoutMs);
  }
  #clearTimer(): void {
    if (this.#timer !== undefined) { clearTimeout(this.#timer); this.#timer = undefined; }
  }
}
